/**
 * DA-431: Utility for persisting substitute application data through authentication flow.
 * Used when unauthenticated users want to apply to duties before registering.
 *
 * Flow:
 * 1. Anonymous user clicks "Apply" on marketplace duty
 * 2. On click: encode data in URL, redirect to app sign-up (app.meindienstarzt.de)
 * 3. Sign-up page decodes URL param and stores in localStorage
 * 4. After registration: restore data and submit application
 *
 * Cross-domain support via URL query params:
 * Since localStorage is origin-bound, we use base64-encoded URL params to transfer
 * data from marketing site (meindienstarzt.de) to app (app.meindienstarzt.de)
 */

import { buildAuthFlowRedirectUrl } from '@/lib/auth-flow-transfer';
import { APP_URL } from '@/lib/app-url';

const STORAGE_KEY = 'pendingSubstituteApplication';

export type PendingSubstituteApplication = {
  serviceId: string; // The duty ID to apply to
  serviceTitle?: string; // Optional display title
  timestamp: number;
};

/**
 * Store pending application data in localStorage
 * @param data Application data (timestamp added automatically)
 */
export const setPendingSubstituteApplication = (data: Omit<PendingSubstituteApplication, 'timestamp'>): void => {
  const fullData: PendingSubstituteApplication = {
    ...data,
    timestamp: Date.now(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(fullData));
};

/**
 * Retrieve pending application data from localStorage
 * @returns Application data or null if not found/expired
 */
export const getPendingSubstituteApplication = (): PendingSubstituteApplication | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw) as PendingSubstituteApplication;

    // Expire after 1 hour
    const ONE_HOUR = 60 * 60 * 1000;
    if (Date.now() - data.timestamp > ONE_HOUR) {
      clearPendingSubstituteApplication();
      return null;
    }

    return data;
  } catch {
    clearPendingSubstituteApplication();
    return null;
  }
};

/**
 * Clear pending application data from localStorage
 */
export const clearPendingSubstituteApplication = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Check if valid pending application exists
 * @returns true if valid pending application exists
 */
export const hasPendingSubstituteApplication = (): boolean => {
  return getPendingSubstituteApplication() !== null;
};

// =============================================================================
// DA-431: Cross-domain URL encoding/decoding helpers
// =============================================================================

/**
 * Decode application data from URL param (used by app sign-up page)
 * @param encoded Base64 URL-safe encoded string from URL param
 * @returns Decoded application data or null if invalid/expired
 */
export const decodeSubstituteApplicationFromUrl = (encoded: string): PendingSubstituteApplication | null => {
  try {
    const json = decodeURIComponent(atob(encoded));
    const data = JSON.parse(json) as PendingSubstituteApplication;

    // Validate required fields
    if (!data.serviceId) {
      console.warn('[decodeSubstituteApplicationFromUrl] Missing required serviceId');
      return null;
    }

    // Check expiration (1 hour)
    const ONE_HOUR = 60 * 60 * 1000;
    if (Date.now() - data.timestamp > ONE_HOUR) {
      console.warn('[decodeSubstituteApplicationFromUrl] Data expired');
      return null;
    }

    return data;
  } catch (err) {
    console.error('[decodeSubstituteApplicationFromUrl] Failed to decode:', err);
    return null;
  }
};

/**
 * Build the redirect URL for application flow
 * @param data Application data to transfer
 * @param appUrl Base URL of the app (default: https://app.meindienstarzt.de)
 * @returns Full redirect URL with encoded application data
 */
export const buildSubstituteApplyRedirectUrl = (transferToken: string, appUrl = APP_URL): string => {
  return buildAuthFlowRedirectUrl({
    appUrl,
    intent: 'apply',
    transferToken,
  });
};

/**
 * Process applicationData URL param on sign-up page
 * Decodes the param and stores in localStorage for later use
 * @returns true if valid application data was found and stored
 */
export const processSubstituteApplicationFromUrl = (): boolean => {
  const searchParams = new URLSearchParams(window.location.search);
  const encodedData = searchParams.get('applicationData');

  if (!encodedData) {
    return false;
  }

  const data = decodeSubstituteApplicationFromUrl(encodedData);
  if (!data) {
    return false;
  }

  // Store in localStorage (now on app.meindienstarzt.de origin)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  console.log('[processSubstituteApplicationFromUrl] Application data stored from URL param');
  return true;
};
